import {
  forwardRef,
  useState,
  type ReactNode,
  type RefObject,
  type MouseEvent,
  type ForwardedRef,
} from 'react';
import {
  useTransition,
  animated,
  config,
  type SpringValue,
} from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import { type ReactDOMAttributes } from '@use-gesture/react/dist/declarations/src/types';
import { useMeasure } from 'react-use';
import { type UseMeasureRef } from 'react-use/lib/useMeasure';
import { ModalContent } from '../Modal/ModalContent/ModalContent';
import { ModalOverlay } from '../Modal/ModalOverlay/ModalOverlay';
import { useModalSheetInputIosKeyboard } from '../useModalSheetInputIosKeyboard';
import styles from './Sheet.module.css';

const AnimatedDialogOverlay = animated(ModalOverlay);
const AnimatedDialogContent = animated(ModalContent);

function getHeightViewModel(
  params: Pick<Props, 'height' | 'contentWrapperClassName'>
): {
  containerClassName: string;
  contentWrapperClassName: string;
  bodyClassName: string;
  bounce?: 0;
} {
  const {
    height: variant,
    contentWrapperClassName: contentWrapperClassNameProp,
  } = params;
  switch (variant) {
    case 'full':
      return {
        containerClassName: styles['container-full-screen'],
        contentWrapperClassName: styles['content-wrapper-full-screen'],
        bodyClassName: `${styles['body-full-screen']} ${contentWrapperClassNameProp}`,
        bounce: 0,
      };
    case 'auto':
    case undefined:
      return {
        containerClassName: styles.container,
        contentWrapperClassName: styles['content-wrapper'],
        bodyClassName: `${styles.body} ${contentWrapperClassNameProp}`,
        bounce: 0,
      };
  }
}

type Props = {
  active: boolean;
  testid?: string;
  children: ReactNode;
  ariaLabel: string;
  initialFocusRef:
    | RefObject<HTMLButtonElement>
    | RefObject<HTMLInputElement>
    | undefined;
  onRequestDismiss: () => void;
  onMouseDown?: (e: MouseEvent) => void;
  height?: 'auto' | 'full';
  contentWrapperClassName?: string;
  className?: string;
  contentClassName?: string;
  contentBodyClassName?: string;
  swipeCloseSegmentEvent?: () => void;
  touchBarClassName?: string;
  showTouchBar?: boolean;
};

export const Sheet = forwardRef(
  (props: Props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const {
      active,
      ariaLabel,
      onRequestDismiss,
      initialFocusRef,
      testid,
      onMouseDown,
      className,
      contentClassName,
      contentBodyClassName,
      swipeCloseSegmentEvent,
      touchBarClassName,
      showTouchBar = true,
    } = props;
    const [ref, { height }] = useMeasure<HTMLDivElement>();
    const [dragY, setDragY] = useState(0);

    const {
      containerClassName,
      bodyClassName,
      contentWrapperClassName,
      bounce,
    } = getHeightViewModel(props);

    const dragPct = height === 0 ? 0 : dragY / height;
    const transitions = useTransition(active, {
      config: {
        ...config.stiff,
        bounce,
      },
      from: {
        transform: `translate3d(0, 100%, 0)`,
        opacity: 0,
      },
      update: {
        transform: `translate3d(0, ${dragPct * 100}%, 0)`,
        opacity: 1,
      },
      enter: {
        transform: `translate3d(0, 0%, 0)`,
        opacity: 1,
      },
      leave: {
        transform: `translate3d(0, 100%, 0)`,
        opacity: 0,
      },
    });

    const drag = useGesture({
      onDrag(state) {
        setDragY(state.xy[1] - state.initial[1]);
      },
      onDragEnd(state) {
        const { direction } = state;
        if (direction[1] === 1) {
          onRequestDismiss();

          if (swipeCloseSegmentEvent) {
            swipeCloseSegmentEvent();
          }
        }
        setDragY(0);
      },
    });

    return transitions(({ transform, opacity }, item) => {
      if (item === false) {
        return null;
      }

      const spring = { transform };

      return (
        <AnimatedDialog
          forwardedRef={forwardedRef}
          onRequestDismiss={onRequestDismiss}
          testid={testid}
          opacity={opacity}
          containerClassName={containerClassName}
          className={className}
          item={item}
          initialFocusRef={initialFocusRef}
          ariaLabel={ariaLabel}
          spring={spring}
          ref={ref}
          contentWrapperClassName={contentWrapperClassName}
          contentClassName={contentClassName}
          onMouseDown={onMouseDown}
          bodyClassName={bodyClassName}
          contentBodyClassName={contentBodyClassName}
          showTouchBar={showTouchBar}
          touchBarClassName={touchBarClassName}
          drag={drag}
          children={props.children}
        />
      );
    });
  }
);

type AnimatedDialogProps = {
  forwardedRef: ForwardedRef<HTMLDivElement>;
  onRequestDismiss: () => void;
  testid?: string;
  opacity: SpringValue<number>;
  containerClassName: string;
  className?: string;
  item: boolean;
  initialFocusRef:
    | RefObject<HTMLButtonElement>
    | RefObject<HTMLInputElement>
    | undefined;
  ariaLabel: string;
  spring: { transform: SpringValue<string> };
  ref: UseMeasureRef<HTMLDivElement>;
  contentWrapperClassName: string;
  contentClassName?: string;
  onMouseDown?: (e: MouseEvent) => void;
  bodyClassName: string;
  contentBodyClassName?: string;
  showTouchBar: boolean;
  touchBarClassName?: string;
  drag: () => ReactDOMAttributes;
  children: ReactNode;
};

function AnimatedDialog(props: AnimatedDialogProps) {
  const {
    forwardedRef,
    onRequestDismiss,
    testid,
    opacity,
    containerClassName,
    className,
    item,
    initialFocusRef,
    ariaLabel,
    spring,
    ref,
    contentWrapperClassName,
    contentClassName,
    onMouseDown,
    bodyClassName,
    contentBodyClassName,
    showTouchBar,
    touchBarClassName,
    drag,
  } = props;

  const { overlayRef } = useModalSheetInputIosKeyboard();

  return (
    <AnimatedDialogOverlay
      ref={(element) => {
        if (typeof forwardedRef === 'object' && forwardedRef !== null) {
          forwardedRef.current = element;
        }
        overlayRef.current = element;
      }}
      onDismiss={onRequestDismiss}
      data-testid={testid}
      style={{
        opacity,
        transition: 'height 200ms',
      }}
      className={`${containerClassName} ${className}`}
      isOpen={item}
      initialFocusRef={initialFocusRef}
    >
      <AnimatedDialogContent
        ariaLabel={ariaLabel}
        data-testid={`${testid}-body`}
        style={spring}
        ref={ref}
        className={`${contentWrapperClassName} ${
          contentClassName === undefined ? '' : contentClassName
        }`}
        onMouseDown={(e) => {
          onMouseDown ? onMouseDown(e) : null;
        }}
      >
        <div
          data-testid={`${testid}-body-wrapper`}
          className={`${bodyClassName} ${contentBodyClassName}`}
        >
          {showTouchBar ? (
            <span
              data-testid={`${testid}-touch-bar`}
              className={
                touchBarClassName ? touchBarClassName : styles['touch-bar']
              }
              {...drag()}
            ></span>
          ) : null}
          {props.children}
        </div>
      </AnimatedDialogContent>
    </AnimatedDialogOverlay>
  );
}
